import { apiClient, handleApiRequest } from './client'

export interface SneakPeekLesson {
  entry_id: string
  time_slot_id: string | null
  start_time: string | null
  subject_id: string | null
  subject_name: string | null
  klass_name: string | null
  room_name: string | null
  selectable: boolean
  status: string | null
}

export interface SneakPeekPackageRow {
  name: string
  code: string | null
  lesson_ids: string[]
  other_days: string[]
}

export interface SneakPeekDay {
  date: string
  mark: string | null
  count: number | null
  zero: boolean
  names: string[]
  codes?: (string | null)[]
  combination: string | null
  whole_day_name: string | null
  whole_day_names?: string[]
  empty: boolean
  counts?: boolean
  lessons?: SneakPeekLesson[]
  packages?: SneakPeekPackageRow[]
  cover_matrix?: boolean
  open_lesson_ids?: string[]
}

export interface SneakPeekGlance {
  threshold: number
  days: SneakPeekDay[]
  report_dates?: string[]
}

export interface SneakPeekVersionRow {
  id: string
  saved_at: string
  admin: string
  can_delete?: boolean
}

export interface SneakPeekQueryRow {
  id: string
  absent: string
  date_from: string
  date_to: string
  saved_at: string
  original_admin: string
  can_delete?: boolean
  versions?: SneakPeekVersionRow[]
}

export interface SneakPeekSavedQuery {
  id: string
  teacher_id: string
  absent: string
  date_from: string
  date_to: string
  selected_dates: string[]
  narrowed_days: { date: string; lessons: string[] }[]
  saved_at: string
  original_admin: string
  report: SneakPeekGlance | null
}

export const sneakPeekApi = {
  glance: (payload: {
    teacher_id: string
    date_from: string
    date_to: string
    selected_dates?: string[]
    narrowed_days?: { date: string; lessons: string[] }[]
  }) =>
    handleApiRequest<SneakPeekGlance>(
      apiClient.post('/api/v1/admin/sneak-peek/glance', payload),
    ),
  getThreshold: () =>
    handleApiRequest<{ threshold: number }>(
      apiClient.get('/api/v1/admin/sneak-peek/threshold'),
    ),
  updateThreshold: (threshold: number) =>
    handleApiRequest<{ threshold: number }>(
      apiClient.put('/api/v1/admin/sneak-peek/threshold', { threshold }),
    ),
  listQueries: () =>
    handleApiRequest<{ queries: SneakPeekQueryRow[] }>(
      apiClient.get('/api/v1/admin/sneak-peek/queries'),
    ),
  saveQuery: (payload: {
    query_id?: string
    teacher_id: string
    date_from: string
    date_to: string
    selected_dates?: string[]
    narrowed_days?: { date: string; lessons: string[] }[]
    report?: SneakPeekGlance | null
    version_id?: string
  }) =>
    handleApiRequest<{ id: string; saved_at: string; version_id?: string }>(
      apiClient.post('/api/v1/admin/sneak-peek/queries', payload),
    ),
  openQuery: (id: string) =>
    handleApiRequest<SneakPeekSavedQuery>(
      apiClient.get(`/api/v1/admin/sneak-peek/queries/${id}`),
    ),
  openVersion: (queryId: string, versionId: string) =>
    handleApiRequest<SneakPeekSavedQuery>(
      apiClient.get(`/api/v1/admin/sneak-peek/queries/${queryId}/versions/${versionId}`),
    ),
  deleteQuery: (id: string) =>
    handleApiRequest<{ deleted: boolean }>(
      apiClient.delete(`/api/v1/admin/sneak-peek/queries/${id}`),
    ),
  deleteVersion: (queryId: string, versionId: string) =>
    handleApiRequest<{ deleted: boolean }>(
      apiClient.delete(`/api/v1/admin/sneak-peek/queries/${queryId}/versions/${versionId}`),
    ),
  recalculateQuery: (id: string) =>
    handleApiRequest<SneakPeekGlance>(
      apiClient.post(`/api/v1/admin/sneak-peek/queries/${id}/recalculate`),
    ),
  recalculateVersion: (queryId: string, versionId: string) =>
    handleApiRequest<SneakPeekGlance>(
      apiClient.post(`/api/v1/admin/sneak-peek/queries/${queryId}/versions/${versionId}/recalculate`),
    ),
}
